import {
    Sheet,
    SheetHeader,
    Heading,
    Switch,
    Text,
    Flex,
    Button,
    Divider,
    Icon,
} from "@amzn/storm-ui";
import { exclamationTriangle } from "@amzn/storm-ui-icons";
import React from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import type {
    UserPreference,
    UserPreferenceList,
} from "../../store/userPreferences";
import {
    UserPreferenceData,
    UserPreferencesStore,
} from "../../store/userPreferences";

const StickyFooter = styled(Flex)`
    position: "sticky",
    bottom: 0,
    border-top: "1px solid #e0e0e0",
`;

interface SettingsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SettingsSidebar: React.FunctionComponent<SettingsSidebarProps> = ({
    isOpen,
    onClose,
}) => {
    const [storedPreferences, savePreferences] = useStore(
        UserPreferencesStore,
        (state) => [state.preferences, state.setPreferences],
    );

    const [localPreferences, setLocalPreferences] =
        React.useState<UserPreferenceList>(storedPreferences);

    const handleSettingToggle = (preference: UserPreference, value: string) => {
        setLocalPreferences({
            ...localPreferences,
            [preference]: value,
        });
    };

    const handleCancel = () => {
        onClose();
    };

    const handleSave = () => {
        savePreferences(localPreferences);
        onClose();

        // Refresh the page to apply changes
        window.location.reload();
    };

    React.useEffect(() => {
        if (isOpen) {
            setLocalPreferences(storedPreferences);
        }
    }, [isOpen, storedPreferences]);

    return (
        <Sheet
            sheetElementId="ihub-portal"
            padding="none"
            data-testid="settings-sidebar"
            isOpen={isOpen}
            onClose={onClose}
            side="end"
            header={
                <SheetHeader closeButtonPosition="none">
                    <Heading renderAs="h2">User Preferences</Heading>
                </SheetHeader>
            }
            closeButton={{
                type: "icon",
                label: "Close user preferences",
            }}
            style={{ maxWidth: "600px" }}
        >
            <Flex
                direction="column"
                justifyContent="space-between"
                style={{ minHeight: "calc(100vh - 2.6rem - 48px)" }} // 100% viewport - size of header (2.6rem font size + 48px padding)
                fullWidth
            >
                <Flex direction="column" padding="large">
                    <Heading renderAs="h3">Early Access Opt-In</Heading>
                    <Text withMargin>
                        Get early access to upcoming IHub features and help
                        shape their development with your feedback. Select any
                        feature below to opt in and start testing.
                    </Text>
                    <Text size="xsmall" style={{ color: "#ab571f" }}>
                        <Icon type={exclamationTriangle} size="xsmall" /> Early
                        access features are in active development and may
                        contain bugs. You can opt out at any time from this
                        screen.
                    </Text>
                    <Flex
                        direction="column"
                        rowGap="none"
                        columnGap="none"
                        fullWidth
                    >
                        {Object.keys(localPreferences).map((setting, index) => {
                            const key = setting as UserPreference;
                            const metadata = UserPreferenceData[key];
                            return (
                                <React.Fragment key={key}>
                                    {index > 0 && (
                                        <Divider
                                            marginBlockStart="none"
                                            marginBlockEnd="none"
                                        />
                                    )}
                                    <Flex
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        columnGap="base"
                                        paddingBlockStart="base"
                                        paddingBlockEnd="base"
                                        fullWidth
                                    >
                                        <Flex
                                            direction="column"
                                            rowGap="xxxsmall"
                                            flex="1"
                                        >
                                            <Text weight="bold">
                                                {metadata.title}
                                            </Text>
                                            <Text
                                                size="small"
                                                color="secondary"
                                            >
                                                {metadata.description}
                                            </Text>
                                        </Flex>
                                        <Switch
                                            id={`setting-${key}`}
                                            toggled={
                                                localPreferences[key] === "true"
                                            }
                                            onChange={() => {
                                                const newValue =
                                                    localPreferences[key] ===
                                                    "true"
                                                        ? "false"
                                                        : "true";
                                                handleSettingToggle(
                                                    key,
                                                    newValue,
                                                );
                                            }}
                                            switchLabel={`Toggle ${metadata.title}`}
                                        />
                                    </Flex>
                                </React.Fragment>
                            );
                        })}
                    </Flex>
                </Flex>
                <StickyFooter
                    direction="row"
                    justifyContent="flex-end"
                    columnGap="small"
                    padding="large"
                    fullWidth
                >
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>
                        Save Preferences
                    </Button>
                </StickyFooter>
            </Flex>
        </Sheet>
    );
};
